import { motion } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";

const BenefitsSection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="relative overflow-hidden theme-bg-primary py-20">
      {/* ================= Background Glow ================= */}

      <div
        className="
          absolute
          -left-32
          top-16
          h-[260px]
          w-[260px]
          rounded-full
          bg-[color:var(--brand-primary)]/10
          blur-[150px]
        "
      />

      <div
        className="
          absolute
          right-0
          bottom-0
          h-[260px]
          w-[260px]
          rounded-full
          bg-[color:var(--brand-primary)]/10
          blur-[150px]
        "
      />

      {/* Grid Pattern */}

      <div
        className="
          absolute
          inset-0
          bg-[linear-gradient(rgba(128,128,128,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(128,128,128,0.08)_1px,transparent_1px)]
          bg-[size:70px_70px]
        "
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="
            overflow-hidden
            rounded-[24px]
            sm:rounded-[28px]
            border
            theme-border
            theme-card
            shadow-[0_18px_60px_var(--shadow-color)]
          "
        >
          {/* ================= Header ================= */}

          <div
            className="
              bg-gradient-to-r
              from-[color:var(--brand-hover)]
              via-[color:var(--brand-primary)]
              to-[color:var(--brand-light)]
              px-5
              py-6
              sm:px-8
              sm:py-8
            "
          >
            <div className="flex items-center gap-4 sm:gap-5">
              <div
                className="
                  flex
                  h-12
                  w-12
                  sm:h-14
                  sm:w-14
                  items-center
                  justify-center
                  rounded-2xl
                  bg-white/15
                  backdrop-blur-xl
                "
              >
                <Sparkles size={24} className="text-white" />
              </div>

              <div>
                <p
                  className="
                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-[3px]
                    text-white/80
                  "
                >
                  WHY CHOOSE US
                </p>

                <h2
                  className="
                    mt-1
                    text-2xl
                    font-bold
                    leading-tight
                    text-white
                    sm:text-3xl
                    lg:text-4xl
                  "
                >
                  {data.title}
                </h2>
              </div>
            </div>
          </div>

          {/* ================= Description ================= */}

          <div className="px-5 pt-7 pb-5 sm:px-8 sm:pt-9">
            <p
              className="
                max-w-5xl
                text-[15px]
                leading-7
                theme-text-muted
                sm:text-base
                lg:text-lg
                lg:leading-8
              "
            >
              Discover how our professional legal services simplify business
              compliance, reduce legal risks, accelerate registrations, ensure
              government compliance, and help your business grow with confidence
              through expert guidance and end-to-end support.
            </p>

            {/* ================= Benefits Grid ================= */}

            <div className="mt-8 grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
              {data.items.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.45,
                    delay: index * 0.07,
                  }}
                  whileHover={{
                    y: -4,
                  }}
                  className="
                    group
                    flex
                    items-start
                    gap-4
                    rounded-2xl
                    border
                    theme-border
                    theme-card-secondary
                    px-4
                    py-4
                    sm:px-5
                    sm:py-5
                    transition-all
                    duration-300
                    hover:border-[color:var(--brand-primary)]/40
                    hover:bg-[color:var(--bg-tertiary)]
                    hover:shadow-[0_0_25px_rgba(0,0,0,0.12)]
                  "
                >
                  {/* Icon */}

                  <div
                    className="
                      flex
                      h-10
                      w-10
                      sm:h-12
                      sm:w-12
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      bg-[color:var(--brand-primary)]/10
                      transition-all
                      duration-300
                      group-hover:bg-[color:var(--brand-primary)]
                    "
                  >
                    <CheckCircle2
                      size={20}
                      className="
                        brand-color
                        transition-all
                        duration-300
                        group-hover:text-white
                      "
                    />
                  </div>

                  {/* Benefit Text */}

                  <div className="flex-1">
                    <p
                      className="
                        text-[15px]
                        leading-6
                        theme-secondary-text
                        transition-colors
                        duration-300
                        group-hover:theme-text-primary
                        sm:text-base
                        sm:leading-7
                      "
                    >
                      {benefit}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;